import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { register } from '../services/authService';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from '@/components/shadcn/card';
import { Button } from '@/components/shadcn/button';

function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { setIsAuthenticated, login } = useAuth();
  //const { setIsAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  // Plan can be pre-selected from the landing page (platform-only and 1-1 plans). Whitelist it —
  // anything else is ignored so a tampered query param can't pick an arbitrary plan.
  const [searchParams] = useSearchParams();
  const PLATFORM_PLANS = ['platform_only', 'platform_only_family'];
  const SELECTABLE_PLANS = [...PLATFORM_PLANS, 'one_to_one'];
  const rawPlan = searchParams.get('plan');
  const selectedPlan = SELECTABLE_PLANS.includes(rawPlan) ? rawPlan : null;
  const isPlatformPlan = PLATFORM_PLANS.includes(rawPlan);
  const isOneToOnePlan = selectedPlan === 'one_to_one';

  // Add validation functions
  const validateEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailPattern.test(email);
  };

  const validateMobileNumber = (number) => {
    const cleanNumber = number.replace(/[\s\-()]/g, '');
    // Accept any mobile number format - international or local
    // Must have at least 7 digits, can start with + and country code
    const mobileRegex = /^\+?[\d]{7,20}$/;
    return mobileRegex.test(cleanNumber);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate email
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate mobile number
    const cleanMobileNumber = mobileNumber.replace(/[\s\-()]/g, '');
    if (!validateMobileNumber(cleanMobileNumber)) {
      setError('Please enter a valid mobile number');
      return;
    }

    try {
      const result = await register(name, email, cleanMobileNumber, password, selectedPlan);
      if (result.success) {
        login(result.userData);

        // Check subscription status
        if (result.userData.accessStatus === 'inactive') {
          navigate('/onboarding');
        } else {
          // Navigate based on isTutor status
          navigate(result.userData.isTutor ? '/tutor-upcoming-lessons' : '/upcoming-lessons');
        }
      } else {
        setError(result.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7FAEE] py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md  border-gray-300">
        <CardHeader>
          <CardTitle className="text-2xl text-center text-[#5C3BD1] font-fredoka">Create your account</CardTitle>
          <CardDescription className="text-center">
            Get started with your parental account
          </CardDescription>
        </CardHeader>

        <CardContent>
          <p className="text-center text-sm text-gray-600 mb-4">
            {isPlatformPlan ? (
              <>Self-paced maths platform. 7-day free trial - <strong>no card details required</strong>.</>
            ) : isOneToOnePlan ? (
              <>1-1 personalised tuition with a dedicated tutor. 7-day free trial - you won't be charged until your trial ends.</>
            ) : (
              <>Live expert-led lessons from £15 an hour. 7-day free trial - <strong>no card details required</strong>.</>
            )}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Parent / Guardian Name
              </label>
              <input
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm max-md:text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Email
              </label>
              <input
                type="email"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm max-md:text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                pattern="[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}"
                title="Please enter a valid email address"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Mobile Number
              </label>
              <input
                type="tel"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm max-md:text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="+1234567890"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                pattern="^\+?[\d\s\-\(\)]{7,20}$"
                title="Please enter a valid mobile number"
                required
              />
              <p className="text-sm text-gray-500">
                Enter your mobile number (e.g., +1234567890 or 07123456789)
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm max-md:text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-red-500 text-sm">{error}</div>
            )}

            <Button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#5C3BD1] hover:bg-[#2F1E6B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5C3BD1] max-md:min-h-[44px]"
            >
              Sign up
            </Button>
          </form>
        </CardContent>

        <CardFooter className="flex justify-center">
          <Link to="/login" className="font-medium text-[#5C3BD1] hover:text-[#2F1E6B]">
            Already have an account? Sign in
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}

export default Register;
